import { useEffect, useState } from 'react'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { TimelineEvent } from '@/frontend/features/Fde/types'

export default function useProjectCalendarEvents(projectId: string, month: string, enabled: boolean) {
  const scope = `${projectId}:${month}:${enabled}`
  const [state, setState] = useState<{ scope: string; events: TimelineEvent[]; loading: boolean; failed: boolean }>({ scope: '', events: [], loading: true, failed: false })
  const [revision, setRevision] = useState(0)

  useEffect(() => {
    if (!enabled) return
    let active = true
    let request: AbortController | null = null
    async function load() {
      request?.abort()
      const controller = new AbortController()
      request = controller
      setState((current) => ({ scope, events: current.scope === scope ? current.events : [], loading: true, failed: false }))
      try {
        const response = await fetch(fdeProjectPath(fdeRoutes.timelineEvents, projectId, { month, time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone }), {
          headers: { Accept: 'application/json' }, signal: controller.signal, cache: 'no-store',
        })
        if (!response.ok) throw new Error('Calendar request failed')
        const payload = await response.json() as { events: TimelineEvent[] }
        if (active && request === controller) setState({ scope, events: payload.events, loading: false, failed: false })
      } catch {
        if (active && request === controller && !controller.signal.aborted) setState({ scope, events: [], loading: false, failed: true })
      }
    }
    void load()
    // Match management's refresh cadence, including meetings updated by agents.
    const refresh = () => { if (document.visibilityState === 'visible') void load() }
    const timer = window.setInterval(refresh, 30_000)
    window.addEventListener('focus', refresh)
    return () => { active = false; request?.abort(); window.clearInterval(timer); window.removeEventListener('focus', refresh) }
  }, [projectId, month, enabled, scope, revision])

  // Never display a previous project's or previous month's response while loading.
  return {
    events: enabled && state.scope === scope ? state.events : [],
    loading: enabled && (state.scope !== scope || state.loading),
    failed: enabled && state.scope === scope && state.failed,
    retry: () => setRevision((value) => value + 1),
  }
}
